import { useCallback } from "react";
import { upsertBooking } from "../../lib/company";
import { bookingAction, openBooking, sendBookingMessage } from "../../lib/companyApi";
import type { BookingAction } from "../../lib/companyApi";
import { useCompany } from "../../lib/companyContext";
import { errorText } from "../../lib/api";

/**
 * Booking decisions and messages through the API; the booking the API sends
 * back replaces the store's copy. Each call resolves to an error message, or
 * "" when it worked.
 */
export function useBookingApi() {
  const { run } = useCompany();

  const apply = useCallback(
    async (call: Promise<Parameters<typeof upsertBooking>[1]>): Promise<string> => {
      try {
        run(upsertBooking, await call);
        return "";
      } catch (err) {
        return errorText(err);
      }
    },
    [run],
  );

  return {
    act: useCallback(
      (ref: string, action: BookingAction, body?: Record<string, unknown>) => apply(bookingAction(ref, action, body)),
      [apply],
    ),
    send: useCallback((ref: string, text: string) => apply(sendBookingMessage(ref, text)), [apply]),
    /** Loads the booking and marks its messages as read. */
    open: useCallback((ref: string) => apply(openBooking(ref)), [apply]),
  };
}
